import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { vneApi, type VneGap, type VneReportListItem } from "../../api/vneApi";
import { useAuth } from "../../auth/AuthContext";
import { canAccessEasyRetailReports } from "../../auth/roles";

function toLocal(value?: string | null) {
  if (!value) return "-";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "-";
  return d.toLocaleString();
}

function statusBadge(status?: string) {
  const s = String(status || "").toUpperCase();
  if (s === "OK") return "text-bg-success";
  if (s === "PARTIAL") return "text-bg-warning";
  if (s === "ERROR") return "text-bg-danger";
  return "text-bg-secondary";
}

function severityBadge(severity: string) {
  const s = String(severity || "").toUpperCase();
  if (s === "HIGH") return "text-bg-danger";
  if (s === "MEDIUM") return "text-bg-warning";
  if (s === "LOW") return "text-bg-secondary";
  return "text-bg-info";
}

function GapTable({ gaps }: { gaps: VneGap[] }) {
  if (!gaps.length) {
    return <div className="text-muted">Nessun elemento rilevato.</div>;
  }
  return (
    <div className="table-responsive">
      <table className="table table-sm align-middle">
        <thead>
          <tr>
            <th>Severità</th>
            <th>Area</th>
            <th>Rilevazione</th>
            <th>Azione suggerita</th>
          </tr>
        </thead>
        <tbody>
          {gaps.map((g) => (
            <tr key={g.id}>
              <td><span className={`badge ${severityBadge(g.severity)}`}>{g.severity}</span></td>
              <td>{g.area}</td>
              <td>
                <div className="fw-semibold">{g.title}</div>
                <div className="small text-muted">{g.detail}</div>
              </td>
              <td className="small">{g.action || "-"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ReportList({ onSelect }: { onSelect: (id: string) => void }) {
  const { token } = useAuth();
  const [items, setItems] = useState<VneReportListItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError("");
    try {
      const res = await vneApi.list(token, 50);
      setItems(res.items || []);
    } catch (e: any) {
      const msg = e?.message || "Errore caricamento report";
      setError(msg.includes("403") ? "Accesso negato. Chiedi profilo Operatore/Manager/Admin." : msg);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <div className="card shadow-sm">
      <div className="card-body">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h2 className="h6 mb-0">Report VNE salvati</h2>
          <button type="button" className="btn btn-sm btn-outline-primary" onClick={() => void load()} disabled={loading}>
            Aggiorna
          </button>
        </div>
        {error ? <div className="alert alert-danger py-2">{error}</div> : null}
        <div className="table-responsive">
          <table className="table table-hover table-sm align-middle mb-0">
            <thead>
              <tr>
                <th>Data</th>
                <th>POS</th>
                <th>Host</th>
                <th>Stato</th>
                <th>VNE</th>
                <th>Easy Retail</th>
                <th>Riepilogo</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {items.length === 0 ? (
                <tr>
                  <td colSpan={8} className="text-muted">
                    {loading ? "Caricamento..." : "Nessun report. Avvia analisi da NATPOS sulla cassa."}
                  </td>
                </tr>
              ) : null}
              {items.map((r) => (
                <tr key={r.id}>
                  <td className="small">{toLocal(r.analyzedAt)}</td>
                  <td>
                    <div>{r.posCode || "-"}</div>
                    <div className="small text-muted">{r.location || ""}</div>
                  </td>
                  <td className="small">{r.hostname || "-"}</td>
                  <td><span className={`badge ${statusBadge(r.status)}`}>{r.status}</span></td>
                  <td className="small">{r.summary?.vneDetected ? "Sì" : "No"}</td>
                  <td className="small">{r.summary?.easyRetailDetected ? "Sì" : "No"}</td>
                  <td className="small">{r.summary?.headline || "-"}</td>
                  <td>
                    <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => onSelect(r.id)}>
                      Dettaglio
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function ReportDetail({ reportId }: { reportId: string }) {
  const { token } = useAuth();
  const [data, setData] = useState<Awaited<ReturnType<typeof vneApi.getById>> | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!token || !reportId) return;
    setLoading(true);
    setError("");
    vneApi
      .getById(token, reportId)
      .then(setData)
      .catch((e: any) => setError(e?.message || "Errore"))
      .finally(() => setLoading(false));
  }, [token, reportId]);

  const report = data?.report;
  const gaps = useMemo(() => (Array.isArray(report?.gaps) ? report!.gaps : []), [report]);
  const findings = (report?.findings || {}) as Record<string, any>;
  const firebird = (findings.firebird || {}) as Record<string, any>;
  const processes = (findings.processes || []) as Array<{ name: string; running: boolean; matchedProcess?: string }>;
  const uiFeatures = (findings.easyRetailVneFeatures || []) as Array<{ label: string; role: string; dataFlow: string }>;
  const commLayers = ((findings.communicationModel || {}).layers || []) as Array<{ from: string; to: string; protocol: string }>;
  const configHits = (findings.configHits || []) as Array<{ file: string; snippets?: string[] }>;
  const driverHits = (findings.driverHits || []) as Array<{ name: string; path: string }>;

  if (loading) return <div className="text-muted">Caricamento report...</div>;
  if (error) return <div className="alert alert-danger py-2">{error}</div>;
  if (!report) return null;

  return (
    <div className="d-grid gap-3">
      <div className="card shadow-sm">
        <div className="card-body">
          <div className="d-flex flex-wrap justify-content-between gap-2 mb-2">
            <div>
              <h2 className="h5 mb-1">Report collegamento VNE</h2>
              <div className="text-muted small">
                {report.posCode || "-"} · {report.hostname || "-"} · {toLocal(report.analyzedAt)}
              </div>
            </div>
            <span className={`badge ${statusBadge(report.status)} align-self-start`}>{report.status}</span>
          </div>
          <p className="mb-0">{(report.summary as any)?.headline || "Analisi completata"}</p>
        </div>
      </div>

      <div className="row g-3">
        <div className="col-md-4">
          <div className="card shadow-sm h-100">
            <div className="card-body">
              <h3 className="h6">PC cassa (runtime)</h3>
              <ul className="small mb-0">
                <li>VNE rilevato: {(report.summary as any)?.vneDetected ? "sì" : "no"}</li>
                <li>Easy Retail rilevato: {(report.summary as any)?.easyRetailDetected ? "sì" : "no"}</li>
                <li>Processi monitorati: {processes.length}</li>
                <li>Processi attivi: {processes.filter((p) => p.running).map((p) => p.matchedProcess || p.name).join(", ") || "-"}</li>
              </ul>
            </div>
          </div>
        </div>
        <div className="col-md-4">
          <div className="card shadow-sm h-100">
            <div className="card-body">
              <h3 className="h6">Database Easy Retail</h3>
              <ul className="small mb-0">
                <li>Firebird: {firebird.connected ? "connesso" : "non connesso"}</li>
                <li>Tabelle VNE: {(firebird.vneTables || []).join(", ") || "-"}</li>
                <li>Tabelle pagamento: {(firebird.paymentTables || []).slice(0, 5).join(", ") || "-"}</li>
              </ul>
            </div>
          </div>
        </div>
        <div className="col-md-4">
          <div className="card shadow-sm h-100">
            <div className="card-body">
              <h3 className="h6">Tracce pagamento (7 gg)</h3>
              <ul className="small mb-0">
                {(firebird.salePaymentCodes || []).slice(0, 5).map((x: { code: unknown; count: number }, i: number) => (
                  <li key={i}>Scontrino cod. {String(x.code)}: {x.count}</li>
                ))}
                {(firebird.cashPaymentForms || []).slice(0, 5).map((x: { formId: unknown; count: number }, i: number) => (
                  <li key={`c-${i}`}>Cassetto forma {String(x.formId)}: {x.count}</li>
                ))}
                {!firebird.salePaymentCodes?.length && !firebird.cashPaymentForms?.length ? <li>Nessun campione disponibile</li> : null}
              </ul>
            </div>
          </div>
        </div>
      </div>

      {commLayers.length > 0 ? (
        <div className="card shadow-sm">
          <div className="card-body">
            <h3 className="h6">Come comunicano VNE, Easy Retail e NAT</h3>
            <div className="table-responsive">
              <table className="table table-sm align-middle mb-0">
                <thead>
                  <tr>
                    <th>Da</th>
                    <th>A</th>
                    <th>Protocollo / canale</th>
                  </tr>
                </thead>
                <tbody>
                  {commLayers.map((layer, idx) => (
                    <tr key={idx}>
                      <td className="small">{layer.from}</td>
                      <td className="small">{layer.to}</td>
                      <td className="small text-muted">{layer.protocol}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      ) : null}

      {uiFeatures.length > 0 ? (
        <div className="card shadow-sm">
          <div className="card-body">
            <h3 className="h6">Funzioni Easy Retail collegate a VNE (UI cassa)</h3>
            <ul className="small mb-0">
              {uiFeatures.map((f) => (
                <li key={f.label}>
                  <strong>{f.label}</strong> — {f.role}
                  <div className="text-muted">{f.dataFlow}</div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      ) : null}

      {configHits.length > 0 || driverHits.length > 0 ? (
        <div className="card shadow-sm">
          <div className="card-body">
            <h3 className="h6">Config e driver sul PC cassa</h3>
            {driverHits.length > 0 ? (
              <div className="mb-2">
                <div className="fw-semibold small">Driver / DLL</div>
                <ul className="small mb-0">
                  {driverHits.map((d) => (
                    <li key={d.path}>{d.name} — <code className="small">{d.path}</code></li>
                  ))}
                </ul>
              </div>
            ) : null}
            {configHits.length > 0 ? (
              <div>
                <div className="fw-semibold small">File config con VNE / cassetto elettronico</div>
                <ul className="small mb-0">
                  {configHits.map((c) => (
                    <li key={c.file}>
                      <code className="small">{c.file}</code>
                      {(c.snippets || []).map((s, i) => (
                        <div key={i} className="text-muted">{s}</div>
                      ))}
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
          </div>
        </div>
      ) : null}

      {(findings.integrationHints || []).length > 0 ? (
        <div className="card shadow-sm">
          <div className="card-body">
            <h3 className="h6">Collegamenti rilevati</h3>
            <ul className="small mb-0">
              {(findings.integrationHints as string[]).map((hint, idx) => (
                <li key={idx}>{hint}</li>
              ))}
            </ul>
          </div>
        </div>
      ) : null}

      <div className="card shadow-sm">
        <div className="card-body">
          <h3 className="h6">Analisi e note</h3>
          <GapTable gaps={gaps} />
        </div>
      </div>

      {report.recommendations?.nextSteps?.length ? (
        <div className="card shadow-sm">
          <div className="card-body">
            <h3 className="h6">Prossimi passi</h3>
            <ol className="small mb-0">
              {report.recommendations.nextSteps.map((step, idx) => (
                <li key={idx}>
                  <strong>[{step.priority}]</strong> {step.title} — {step.action}
                </li>
              ))}
            </ol>
          </div>
        </div>
      ) : null}
    </div>
  );
}

export default function VneAnalysisPage() {
  const { id } = useParams();
  const { user } = useAuth();
  const canView = canAccessEasyRetailReports(user?.role);
  const [selectedId, setSelectedId] = useState<string | null>(id || null);

  useEffect(() => {
    if (id) setSelectedId(id);
  }, [id]);

  if (!canView) {
    return (
      <div className="container-fluid py-3">
        <div className="alert alert-warning mb-0">
          <strong>Report VNE non disponibile</strong> per il profilo {user?.role || "VIEWER"}.
          Serve account <strong>Operatore</strong>, <strong>Manager</strong> o <strong>Admin</strong>.
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid py-3">
      <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
        <div>
          <h1 className="h4 mb-1">Collegamento VNE</h1>
          <p className="text-muted mb-0 small">
            Come il cassetto automatico VNE comunica con Easy Retail sul PC cassa. NAT non parla con VNE direttamente:
            legge solo ciò che Easy Retail registra su Firebird.
          </p>
        </div>
        {selectedId ? (
          <Link to="/reports/vne" className="btn btn-sm btn-outline-secondary">
            Torna all&apos;elenco
          </Link>
        ) : (
          <Link to="/reports" className="btn btn-sm btn-outline-secondary">
            Tutti i report
          </Link>
        )}
      </div>

      {!selectedId ? (
        <>
          <div className="alert alert-info py-2 mb-3 small">
            Sul PC cassa apri NATPOS Electron e premi <strong>Analizza collegamento VNE e invia report</strong>.
            Il report compare qui in <strong>Report → Collegamento VNE</strong> (<code>/reports/vne</code>).
          </div>
          <ReportList onSelect={setSelectedId} />
        </>
      ) : (
        <ReportDetail reportId={selectedId} />
      )}
    </div>
  );
}
